import { type Common } from './common.js';
import {
  type Availability,
  type UpdateValues as AvailabilityUpdateValues,
} from './models/availability.js';
import { type EhrProviderInformationPopulated } from './models/ehrProviderInformation.js';
import { type Organization } from './models/organization/index.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import {
  type CreateValues,
  type FindAllOptions,
  type Practice,
  type PracticeAccount,
  type PracticeBasic,
  type PracticeBulkCreateValues,
  type PracticeFull,
  type ProviderPractice,
  type UpdateValues,
} from './models/practice.js';
import { type PracticeGroup } from './models/practiceGroup.js';
import { type ImportValidation } from './models/provider.js';
import { type User } from './models/user.js';

export class API {
  common: Common;
  readonly baseEndpoint = '/practice';

  constructor(common: Common) {
    this.common = common;
  }

  async create(values: CreateValues): Promise<Practice> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, values);
  }

  async createWithPreferences(practices: PracticeBulkCreateValues[]): Promise<Practice> {
    const url = this.common.constructPath(this.baseEndpoint);
    return await this.common.postJSON(url, practices);
  }

  async findAll(query: FindAllOptions = {}): Promise<Practice[]> {
    const url = this.common.constructPath(this.baseEndpoint, query);
    const { practices } = await this.common.getJSON(url);
    return practices;
  }

  async findAllPaginated(
    query: PaginatedQueryOptions<FindAllOptions>
  ): Promise<PaginationResponse<Practice>> {
    const url = this.common.constructPath(this.baseEndpoint, query);
    return await this.common.getJSON(url);
  }

  async findAllBasic(query: FindAllOptions = {}): Promise<PracticeBasic[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/basic`, query);
    const { practices } = await this.common.getJSON(url);
    return practices;
  }

  async findOneById(id: string): Promise<PracticeFull> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { practice } = await this.common.getJSON(url);
    return practice;
  }

  async findOneBasicById(id: string): Promise<PracticeBasic> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/basic`);
    const { practice } = await this.common.getJSON(url);
    return practice;
  }

  async findAllProvidersByPracticeId(id: string): Promise<User[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/providers`);
    const { providers } = await this.common.getJSON(url);
    return providers;
  }

  async addProvider(practiceId: string, providerUserId: string): Promise<ProviderPractice> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/provider`);
    const { added } = await this.common.postJSON(url, { providerUserId });
    return added;
  }

  async addAccount(practiceId: string, accountId: string): Promise<PracticeAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/account/`);
    return await this.common.postJSON(url, { accountId });
  }

  async updateAccount(practiceId: string, accountId: string): Promise<PracticeAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/account/`);
    return await this.common.patchJSON(url, { accountId });
  }

  async deleteAccountByPracticeId(practiceId: string): Promise<{ success: boolean }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/account/`);
    return await this.common.deleteJSON(url);
  }

  async findAccountByPracticeId(practiceId: string): Promise<PracticeAccount> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/account/`);
    return await this.common.getJSON(url);
  }

  async syncCalendar(practiceId: string, formattedDate?: string): Promise<boolean> {
    // formattedDate in YYYY-MM-DD format
    const url = this.common.constructPath(`/calendar/sync`);
    const body = {
      date: formattedDate,
      practiceId,
    };
    const { success } = await this.common.postJSON(url, body);
    return success;
  }

  async update(id: string, values: UpdateValues): Promise<Practice> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    const { practice } = await this.common.patchJSON(url, values);
    return practice;
  }

  async findAllByProviderId(providerId: string): Promise<{
    user: User;
    practices: (Practice & {
      ehrProvider?: EhrProviderInformationPopulated;
    })[];
    practiceGroups: PracticeGroup[];
    organizations: Organization[];
  }> {
    const url = this.common.constructPath(`/provider/${providerId}/practices`);
    return await this.common.getJSON(url);
  }

  async verifyImport(
    values: Record<string, string>[]
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/import/verify`);
    return await this.common.postJSON(url, values);
  }

  async getPracticeAvailability(practiceId: string): Promise<Availability | null> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/availability`);
    return await this.common.getJSON(url);
  }

  async updatePracticeAvailability(
    practiceId: string,
    values: AvailabilityUpdateValues
  ): Promise<Availability> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/availability`);
    return await this.common.patchJSON(url, values);
  }

  async deletePracticeAvailability(practiceId: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${practiceId}/availability`);
    await this.common.deleteJSON(url);
  }
}
